import React, { useMemo, useEffect } from "react";
import { getVal, setVal, RadioList, DropdownMulti } from "oolib";
import { useReportLandingContext } from "../../../../ReportLandingContext";
import { DATA_KEYS } from "../DATA_KEYS";

const normalizeDisplayValue = (displayVal) => {
  if (Array.isArray(displayVal)) return displayVal[0] || "";
  return displayVal || "";
};

const PRESET_OPTIONS = [
  { display: "Top Flow", value: "topFlow" },
  { display: "All", value: "all" },
];

/**
 * Extract unique events at each step level from hierarchical data.
 * Returns [{ step, events: [{ display, value }] }] sorted by count desc within each step.
 */
const getEventsPerStep = (data) => {
  const stepsMap = {};

  const walk = (nodes, step) => {
    if (!nodes?.length) return;
    if (!stepsMap[step]) stepsMap[step] = new Map();
    nodes.forEach((node) => {
      const display = normalizeDisplayValue(node.display);
      stepsMap[step].set(
        display,
        (stepsMap[step].get(display) || 0) + (node.count || 0)
      );
      if (node.children?.length) walk(node.children, step + 1);
    });
  };

  walk(data, 0);

  return Object.entries(stepsMap).map(([step, eventsMap]) => ({
    step: Number(step),
    events: Array.from(eventsMap.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([name]) => ({ display: name, value: name })),
  }));
};

/**
 * Compute hidden events for the "topFlow" preset:
 * Step 0 → hide all except top 1 by count.  Steps 1+ → show all.
 */
const getTopFlowHidden = (data) => {
  if (!data?.length) return {};
  const sorted = [...data].sort(
    (a, b) => (b.count || 0) - (a.count || 0)
  );
  if (sorted.length <= 1) return {};
  const hidden = sorted
    .slice(1)
    .map((n) => normalizeDisplayValue(n.display));
  return { "0": hidden };
};

export const EventFlowVisibilityInput = ({
  thisWidgetConfig,
  setConfig,
  underEditWidgetIdx,
}) => {
  const { widgetDataStore } = useReportLandingContext();
  const data = widgetDataStore?.[thisWidgetConfig.id];

  const currentPreset =
    getVal(thisWidgetConfig, DATA_KEYS.efVisibilityPreset) || "topFlow";
  const savedHidden =
    getVal(thisWidgetConfig, DATA_KEYS.efHiddenEventsPerStep) || {};

  const stepsInfo = useMemo(() => getEventsPerStep(data), [data]);

  // Effective hidden list: driven by preset or manual
  const effectiveHidden = useMemo(() => {
    if (currentPreset === "topFlow") return getTopFlowHidden(data);
    return savedHidden;
  }, [currentPreset, data, savedHidden]);

  // Re-sync config when data changes while topFlow preset is active
  useEffect(() => {
    if (currentPreset !== "topFlow" || !data?.length) return;

    const computed = getTopFlowHidden(data);
    const hasChanged =
      JSON.stringify(computed) !== JSON.stringify(savedHidden);

    if (hasChanged) {
      setConfig((prev) => {
        let mc = JSON.parse(JSON.stringify(prev[underEditWidgetIdx]));
        mc = setVal(mc, DATA_KEYS.efHiddenEventsPerStep, computed);
        const full = JSON.parse(JSON.stringify(prev));
        full[underEditWidgetIdx] = mc;
        return full;
      });
    }
  }, [data, currentPreset]);

  if (!data?.length) return null;

  // --- handlers ---

  const handlePresetChange = (_, presetValue) => {
    setConfig((prev) => {
      let mc = JSON.parse(JSON.stringify(prev[underEditWidgetIdx]));
      mc = setVal(mc, DATA_KEYS.efVisibilityPreset, presetValue);
      mc = setVal(
        mc,
        DATA_KEYS.efHiddenEventsPerStep,
        presetValue === "topFlow" ? getTopFlowHidden(data) : {}
      );
      const full = JSON.parse(JSON.stringify(prev));
      full[underEditWidgetIdx] = mc;
      return full;
    });
  };

  const handleStepChange = (step, selectedValues) => {
    const stepInfo = stepsInfo.find((s) => s.step === step);
    if (!stepInfo) return;

    const newHidden = stepInfo.events
      .filter((opt) => !selectedValues.some((sv) => sv.value === opt.value))
      .map((opt) => opt.value);

    setConfig((prev) => {
      let mc = JSON.parse(JSON.stringify(prev[underEditWidgetIdx]));
      const current =
        getVal(mc, DATA_KEYS.efHiddenEventsPerStep) || {};
      const updated = { ...current };

      if (newHidden.length > 0) {
        updated[String(step)] = newHidden;
      } else {
        delete updated[String(step)];
      }

      mc = setVal(mc, DATA_KEYS.efHiddenEventsPerStep, updated);

      // Manual change resets preset
      if (currentPreset === "topFlow") {
        mc = setVal(mc, DATA_KEYS.efVisibilityPreset, "all");
      }

      const full = JSON.parse(JSON.stringify(prev));
      full[underEditWidgetIdx] = mc;
      return full;
    });
  };

  return (
    <div>
      <RadioList
        id={`ef-visibility-preset-${thisWidgetConfig.id}`}
        label="Visible Events"
        options={PRESET_OPTIONS}
        value={currentPreset}
        onChange={handlePresetChange}
        saveValueAsString
        listType="horizontal"
        S
      />
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "0.5rem",
          marginTop: "0.6rem",
        }}
      >
        {stepsInfo.map(({ step, events }) => {
          if (events.length <= 1) return null;
          const hiddenAtStep = effectiveHidden[String(step)] || [];
          const visible = events.filter(
            (e) => !hiddenAtStep.includes(e.value)
          );
          return (
            <DropdownMulti
              key={`ef-step-${step}-${thisWidgetConfig.id}`}
              label={`Step ${step + 1}`}
              sublabel={
                hiddenAtStep.length > 0
                  ? `${hiddenAtStep.length} event(s) hidden`
                  : undefined
              }
              options={events}
              value={visible}
              onChange={(_, vals) => handleStepChange(step, vals)}
              matchSelectInputWidth
              showSelectAll
            />
          );
        })}
      </div>
    </div>
  );
};
